import {
  AUDIOLOGIST_SKU_OPTIONS,
  AUDIOLOGIST_PRESCRIPTION_TYPE_OPTIONS,
  AUDIOLOGIST_LENS_TYPE_OPTIONS,
  AUDIOLOGIST_INDEX_OPTIONS,
  AUDIOLOGIST_MOUNTING_OPTIONS,
} from "../mocks/va/mockData";
import { formatPrescriptionValue } from "./Utils";
import { shortenLensLabel } from "./audiologistLensSelection";
import {
  isMultiLensPrescription,
  isMultiLensSelection,
  normalizeLensItems,
} from "./prescriptionFormat";
import { getAudiologistBundleDetailRows } from "./vaInvoiceUtils";

function findOption(options, value) {
  return options.find((option) => option.value === value);
}

function formatLineLabel(option) {
  if (!option) return "";
  return option.label.replace(/\s*\(\+\$\d+\)/, "").trim();
}

function hasSummaryValue(value) {
  const text = String(value ?? "").trim();
  if (!text) return false;
  return !["—", "-", "none", "n/a"].includes(text.toLowerCase());
}

function buildBundleProductLines(bundle = {}) {
  return getAudiologistBundleDetailRows(bundle)
    .map((row) => ({
      label: row.label,
      value: Array.isArray(row.items) ? row.items.join(", ") : row.items,
    }))
    .filter((line) => hasSummaryValue(line.value));
}

function formatRxValue(value, type) {
  if (value == null || value === "") return "";
  const raw =
    typeof value === "object" && value != null && "value" in value
      ? value.value
      : value;
  return formatPrescriptionValue(raw, type) || "";
}

function buildEyeLine(formValues, eyeKey) {
  if (!formValues) return "";
  const sph = formatRxValue(formValues?.[`${eyeKey}Sphere_SPH`], "SPH");
  const cyl = formatRxValue(formValues?.[`${eyeKey}Cylinder_CYL`], "CYL");
  const axis = formatRxValue(formValues?.[`${eyeKey}Axis`], "AXIS");
  if (!sph && !cyl && !axis) {
    return "";
  }
  return `${sph || "—"} | ${cyl || "—"} | x${axis || "—"}`;
}

function buildPdLine(formValues, pdType) {
  if (!formValues) return "";
  if (pdType === "One") {
    return formatRxValue(
      formValues.SingularPDValue?.value ?? formValues.SingularPDValue,
      "PD"
    );
  }

  const odPd = formatRxValue(formValues?.ODRightPupillaryDistance_PD, "PD");
  const osPd = formatRxValue(formValues?.OSLeftPupillaryDistance_PD, "PD");
  if (!odPd && !osPd) {
    return "";
  }
  return `OD ${odPd || "—"} · OS ${osPd || "—"}`;
}

function formatLensConfigSummary(entry = {}) {
  const prescriptionType = findOption(
    AUDIOLOGIST_PRESCRIPTION_TYPE_OPTIONS,
    entry?.prescriptionType
  );
  const lensType = findOption(
    AUDIOLOGIST_LENS_TYPE_OPTIONS,
    entry?.lensType
  );
  const indexType = findOption(
    AUDIOLOGIST_INDEX_OPTIONS,
    entry?.indexType
  );
  const mountingType = findOption(
    AUDIOLOGIST_MOUNTING_OPTIONS,
    entry?.mountingType
  );

  return [
    formatLineLabel(prescriptionType),
    formatLineLabel(lensType),
    formatLineLabel(indexType),
    formatLineLabel(mountingType),
  ]
    .filter(hasSummaryValue)
    .join(" · ");
}

function buildLensSelectionLines(lensSelection) {
  if (!lensSelection) return [];

  if (isMultiLensSelection(lensSelection)) {
    return (lensSelection.selections || []).map((entry, index) => ({
      label: shortenLensLabel(entry?.label) || `Lens ${index + 1}`,
      value: formatLensConfigSummary(entry),
    }));
  }

  return [
    {
      label: "Prescription Type",
      value: formatLineLabel(
        findOption(
          AUDIOLOGIST_PRESCRIPTION_TYPE_OPTIONS,
          lensSelection.prescriptionType
        )
      ),
    },
    {
      label: "Lens Type",
      value: formatLineLabel(
        findOption(AUDIOLOGIST_LENS_TYPE_OPTIONS, lensSelection.lensType)
      ),
    },
    {
      label: "Index",
      value: formatLineLabel(
        findOption(AUDIOLOGIST_INDEX_OPTIONS, lensSelection.indexType)
      ),
    },
    {
      label: "Mounting",
      value: formatLineLabel(
        findOption(AUDIOLOGIST_MOUNTING_OPTIONS, lensSelection.mountingType)
      ),
    },
  ].filter((line) => hasSummaryValue(line.value));
}

function buildPrescriptionSummaryLine(entry = {}) {
  const od = buildEyeLine(entry?.formValues, "ODRight");
  const os = buildEyeLine(entry?.formValues, "OSLeft");
  const pd = buildPdLine(entry?.formValues, entry?.pdType);
  const fileName = String(entry?.fileName || "").trim();

  const parts = [];
  if (od) parts.push(`OD ${od}`);
  if (os) parts.push(`OS ${os}`);
  if (pd) parts.push(`PD ${pd}`);
  if (fileName) parts.push(fileName);

  return parts.join(" · ");
}

function buildPrescriptionLines(prescriptionData, { includePrescription = true } = {}) {
  if (!includePrescription) return [];

  const prescriptionSkipped = Boolean(prescriptionData?.skipped);

  if (prescriptionSkipped) {
    return [{ label: "Prescription", value: "Skipped — will add later" }];
  }

  if (isMultiLensPrescription(prescriptionData)) {
    return (prescriptionData?.prescriptions || []).map((entry, index) => {
      if (entry?.skipped) {
        return {
          label: shortenLensLabel(entry?.label) || `Lens ${index + 1} Rx`,
          value: "Not provided — add later",
        };
      }

      const summary = buildPrescriptionSummaryLine(entry);
      const fileName = String(entry?.fileName || "").trim();

      return {
        label: shortenLensLabel(entry?.label) || `Lens ${index + 1} Rx`,
        value: summary || "Entered manually",
        ...(fileName && entry?.fileUrl
          ? { href: entry.fileUrl, isFile: true }
          : {}),
      };
    });
  }

  const formValues = prescriptionData?.formValues;
  const prescriptionFileName = String(prescriptionData?.fileName || "").trim();
  const summary = buildPrescriptionSummaryLine({
    formValues,
    pdType: prescriptionData?.pdType,
    fileName: prescriptionFileName,
  });

  const lines = [];

  if (summary) {
    lines.push({ label: "Prescription", value: summary });
  }

  if (prescriptionFileName && prescriptionData?.fileUrl) {
    lines.push({
      label: "Prescription file",
      value: prescriptionFileName,
      href: prescriptionData.fileUrl,
      isFile: true,
    });
  }

  if (!lines.length) {
    lines.push({ label: "Prescription", value: "Entered manually" });
  }

  return lines;
}

function getLensExtras(lensSelection) {
  if (!lensSelection) return 0;

  const extrasForEntry = (entry = {}) => {
    const lensType = findOption(
      AUDIOLOGIST_LENS_TYPE_OPTIONS,
      entry.lensType
    );
    const indexType = findOption(
      AUDIOLOGIST_INDEX_OPTIONS,
      entry.indexType
    );
    const mountingType = findOption(
      AUDIOLOGIST_MOUNTING_OPTIONS,
      entry.mountingType
    );

    return (
      (lensType?.priceExtra || 0) +
      (indexType?.priceExtra || 0) +
      (mountingType?.priceExtra || 0)
    );
  };

  if (isMultiLensSelection(lensSelection)) {
    return (lensSelection.selections || []).reduce(
      (sum, entry) => sum + extrasForEntry(entry),
      0
    );
  }

  return extrasForEntry(lensSelection);
}

export function buildAudiologistOrderSummary({
  skuCode,
  skuLabel,
  skuPrice,
  bundleCode,
  bundle = null,
  lensSelection,
  prescriptionData,
  quote = null,
}) {
  const mockSku =
    AUDIOLOGIST_SKU_OPTIONS.find((item) => item.code === skuCode) ||
    AUDIOLOGIST_SKU_OPTIONS[0];

  const label = String(skuLabel || "").trim() || mockSku.label;
  const price =
    skuPrice != null && !Number.isNaN(Number(skuPrice))
      ? Number(skuPrice)
      : mockSku.price;

  const lensExtras = getLensExtras(lensSelection);

  const quoteSubtotal =
    quote?.subtotal != null && !Number.isNaN(Number(quote.subtotal))
      ? Number(quote.subtotal)
      : null;
  const quoteTotal =
    quote?.total != null && !Number.isNaN(Number(quote.total))
      ? Number(quote.total)
      : null;

  const estimateSubtotal = price + lensExtras;
  const subtotal = quoteSubtotal != null ? quoteSubtotal : estimateSubtotal;
  const total = quoteTotal != null ? quoteTotal : subtotal;

  const hasLenses = normalizeLensItems(bundle?.lensesItems).length > 0;
  const includePrescription = hasLenses || Boolean(
    prescriptionData && !prescriptionData.skipped
  );

  const lines = [
    { label: "Bundle", value: bundleCode || "" },
    ...buildBundleProductLines(bundle),
    ...buildLensSelectionLines(lensSelection),
    ...buildPrescriptionLines(prescriptionData, { includePrescription }),
  ].filter((line) => hasSummaryValue(line.value));

  return {
    productTitle: `${label} ($${price})`,
    lines,
    total,
    subtotal,
  };
}

export function formatUsd(amount) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return "—";
  return `USD $${value.toFixed(2)}`;
}
